import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboarding } from './onboarding/OnboardingContext';
import { Sidebar, type NavGroup, type NavItem } from './Sidebar';
import { Topbar } from './Topbar';

export type { NavGroup, NavItem };

/** Оболочка: фиксированный сайдбар 240px, на <900px — выезжает с оверлеем; шапка sticky; main до 1440px. */
export function AppShell({
  groups,
  roleLabel,
  companyChip,
  avatarText,
  avatarTitle,
  balance,
  onLogout,
  onStartTour,
  tourDone,
  children,
}: {
  groups: NavGroup[];
  /** «Кабинет партнёра» | «Управляющая компания». */
  roleLabel: string;
  /** «Юридических лиц: N» | имя компании. */
  companyChip?: string;
  avatarText: string;
  avatarTitle?: string;
  /** null/undefined — кнопки баланса нет (нет права billing). */
  balance?: { label: string | null; to: string } | null;
  onLogout: () => void;
  onStartTour: () => void;
  /** «Пройти обучение заново» вместо «Пройти обучение». */
  tourDone: boolean;
  children: ReactNode;
}) {
  const { pathname, search } = useLocation();
  const { tourStep } = useOnboarding();
  const [menuOpen, setMenuOpen] = useState(false);
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem('fishka.studio.compact.v2') === 'true'; } catch { return false; }
  });
  const toggleCompact = () => setCompact(value => {
    try { localStorage.setItem('fishka.studio.compact.v2', String(!value)); } catch { /* Меню работает и без хранилища. */ }
    return !value;
  });

  // Любой переход и любой шаг тура закрывают мобильное меню (behaviour.md §1.5, §3.2).
  useEffect(() => setMenuOpen(false), [pathname, search, tourStep]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className={`marketing-root fk-shell${compact ? ' fk-compact' : ''}${menuOpen ? ' fk-menu-open' : ''}`}>
      {menuOpen && (
        <button type="button" className="fk-shell-overlay" aria-label="Закрыть меню" onClick={() => setMenuOpen(false)} />
      )}
      <Sidebar
        compact={compact}
        onToggleCompact={toggleCompact}
        groups={groups}
        roleLabel={roleLabel}
        onLogout={onLogout}
        onStartTour={() => {
          setMenuOpen(false);
          onStartTour();
        }}
        tourDone={tourDone}
        onNavigate={() => setMenuOpen(false)}
        focusFirst={menuOpen}
      />
      <div className="fk-workspace">
        <Topbar
          menuOpen={menuOpen}
          onToggleMenu={() => setMenuOpen((value) => !value)}
          balance={balance}
          companyChip={companyChip}
          avatarText={avatarText}
          avatarTitle={avatarTitle}
        />
        <main className="fk-main">{children}</main>
      </div>
    </div>
  );
}
